/**
 * Escalas de cor e formatos das variáveis do mapa — funções puras (testadas em
 * `meteo.test.ts`).
 *
 * Cada escala é por FAIXAS (poucas cores), e não contínua: o cache de estilos
 * da camada fica pequeno, e a legenda do painel diz exatamente o que cada cor
 * significa. Sobre o fundo escuro do GOES quem carrega a informação é a
 * luminosidade — por isso nenhum extremo é escuro.
 *
 * O laranja puro é do foco de queimada neste mapa; ele aparece aqui só no meio
 * das escalas quentes, e sempre num símbolo quadrado ou losango com número ao
 * lado, que não se confunde com o ponto do foco.
 */
import { FAIXAS as FAIXAS_CHUVA } from '../stations/series';
import type { Variavel } from './types';

export interface Faixa {
  /** Limite inferior (inclusive) na unidade da tela. */
  min: number;
  cor: string;
  rotulo: string;
}

export interface Escala {
  nome: string;
  /** Nome curto (seletor do painel). */
  curto: string;
  unidade: string;
  faixas: readonly Faixa[];
  /** Os dois extremos da legenda. */
  extremos: [string, string];
}

/**
 * Temperatura do ar. A Amazônia vai de ~20 °C nas madrugadas de friagem a
 * ~40 °C nas tardes da seca: a escala dá mais degraus entre 24 e 39.
 */
const TEMPERATURA: readonly Faixa[] = [
  { min: -Infinity, cor: '#7aa2ff', rotulo: 'abaixo de 18' },
  { min: 18, cor: '#6fd0ff', rotulo: '18–21' },
  { min: 21, cor: '#7fe0c0', rotulo: '21–24' },
  { min: 24, cor: '#b6ec8a', rotulo: '24–27' },
  { min: 27, cor: '#f2e56b', rotulo: '27–30' },
  { min: 30, cor: '#ffc15a', rotulo: '30–33' },
  { min: 33, cor: '#ff8f5a', rotulo: '33–36' },
  { min: 36, cor: '#ff5f6d', rotulo: '36–39' },
  { min: 39, cor: '#e56fe0', rotulo: '39 ou mais' },
];

/**
 * Umidade relativa. Abaixo de 30% o INMET emite alerta de baixa umidade (e o
 * fogo se espalha mais fácil): o seco vai para o ocre, o úmido para o azul —
 * o mesmo eixo seco ↔ cheio do nível dos rios.
 */
const UMIDADE: readonly Faixa[] = [
  { min: -Infinity, cor: '#e0973f', rotulo: 'abaixo de 20' },
  { min: 20, cor: '#e8b870', rotulo: '20–30' },
  { min: 30, cor: '#eedba8', rotulo: '30–45' },
  { min: 45, cor: '#e9eef2', rotulo: '45–60' },
  { min: 60, cor: '#a9d6ee', rotulo: '60–75' },
  { min: 75, cor: '#6fb2e0', rotulo: '75–90' },
  { min: 90, cor: '#4d8fd6', rotulo: '90 ou mais' },
];

/** Vento médio em km/h (a rajada fica na ficha). */
const VENTO: readonly Faixa[] = [
  { min: -Infinity, cor: '#c3ccda', rotulo: 'calmo (até 5)' },
  { min: 5, cor: '#9be3c3', rotulo: '5–10' },
  { min: 10, cor: '#d4ec7a', rotulo: '10–20' },
  { min: 20, cor: '#ffd25a', rotulo: '20–30' },
  { min: 30, cor: '#ff9a5a', rotulo: '30–50' },
  { min: 50, cor: '#ff5f6d', rotulo: '50 ou mais' },
];

/** Pressão ao nível do mar, em hPa: a Amazônia equatorial quase não sai de 1004–1016. */
const PRESSAO: readonly Faixa[] = [
  { min: -Infinity, cor: '#c792ff', rotulo: 'abaixo de 1004' },
  { min: 1004, cor: '#8fa8ff', rotulo: '1004–1007' },
  { min: 1007, cor: '#7fd3ff', rotulo: '1007–1010' },
  { min: 1010, cor: '#9be3c3', rotulo: '1010–1013' },
  { min: 1013, cor: '#f2e56b', rotulo: '1013–1016' },
  { min: 1016, cor: '#ffb070', rotulo: '1016 ou mais' },
];

/** Ponto de orvalho: quanto vapor há no ar, independente da temperatura. */
const ORVALHO: readonly Faixa[] = [
  { min: -Infinity, cor: '#d6a36a', rotulo: 'abaixo de 12' },
  { min: 12, cor: '#e8c48a', rotulo: '12–16' },
  { min: 16, cor: '#eee6c8', rotulo: '16–19' },
  { min: 19, cor: '#b6ec8a', rotulo: '19–22' },
  { min: 22, cor: '#6fdcb0', rotulo: '22–24' },
  { min: 24, cor: '#4fb8d8', rotulo: '24 ou mais' },
];

/**
 * Chuva na hora: as MESMAS faixas e cores dos pluviômetros do CEMADEN — a
 * mesma chuva não pode ter duas cores no mesmo mapa. O primeiro degrau
 * ("sem chuva", quase preto) é clareado aqui: ele pinta um símbolo sólido com
 * número, e não uma gota vazada.
 */
const CHUVA: readonly Faixa[] = FAIXAS_CHUVA.map((f, i) => ({
  min: i === 0 ? -Infinity : f.min,
  cor: i === 0 ? '#8c99ab' : f.cor,
  rotulo: f.rotulo,
}));

export const ESCALAS: Readonly<Record<Variavel, Escala>> = {
  t: { nome: 'Temperatura do ar', curto: 'Temperatura', unidade: '°C', faixas: TEMPERATURA, extremos: ['frio', 'quente'] },
  ur: { nome: 'Umidade relativa do ar', curto: 'Umidade', unidade: '%', faixas: UMIDADE, extremos: ['seco', 'úmido'] },
  vento: { nome: 'Vento médio', curto: 'Vento', unidade: 'km/h', faixas: VENTO, extremos: ['calmo', 'forte'] },
  p: { nome: 'Pressão ao nível do mar', curto: 'Pressão', unidade: 'hPa', faixas: PRESSAO, extremos: ['baixa', 'alta'] },
  chuva: { nome: 'Chuva na última hora', curto: 'Chuva', unidade: 'mm', faixas: CHUVA, extremos: ['sem chuva', 'extrema'] },
  td: { nome: 'Ponto de orvalho', curto: 'Orvalho', unidade: '°C', faixas: ORVALHO, extremos: ['ar seco', 'ar úmido'] },
};

/** Ordem do seletor do painel. */
export const VARIAVEIS: readonly Variavel[] = ['t', 'ur', 'vento', 'chuva', 'p', 'td'];

/** Índice da faixa de um valor. */
export function faixaDe(variavel: Variavel, v: number): number {
  const f = ESCALAS[variavel].faixas;
  let i = 0;
  for (let k = 1; k < f.length; k++) {
    if (v >= f[k]!.min) i = k;
    else break;
  }
  return i;
}

export function corDe(variavel: Variavel, v: number): string {
  return ESCALAS[variavel].faixas[faixaDe(variavel, v)]!.cor;
}

/** Número em português, com `casas` decimais. */
export function numero(v: number, casas = 0): string {
  return v.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas });
}

/**
 * Rótulo compacto do mapa: "31°", "45%", "12", "1011", "2,4". Inteiro sempre
 * que o número decimal não diz nada a mais de relance; a ficha tem a casa.
 */
export function rotuloMapa(variavel: Variavel, v: number): string {
  switch (variavel) {
    case 't':
    case 'td':
      return `${numero(Math.round(v))}°`;
    case 'ur':
      return `${numero(Math.round(v))}%`;
    case 'vento':
      return numero(Math.round(v));
    case 'p':
      return String(Math.round(v));
    case 'chuva':
      return v === 0 ? '0' : v < 10 ? numero(v, 1) : numero(Math.round(v));
  }
}

/** Valor com unidade, para a ficha e o painel: "31,8 °C", "12 km/h". */
export function comUnidade(variavel: Variavel, v: number): string {
  switch (variavel) {
    case 't':
    case 'td':
      return `${numero(v, 1)} °C`;
    case 'ur':
      return `${numero(Math.round(v))}%`;
    case 'vento':
      return `${numero(Math.round(v))} km/h`;
    case 'p':
      return `${numero(v, 1)} hPa`;
    case 'chuva':
      return `${numero(v, 1)} mm`;
  }
}
